import { UIElement } from "./ui_element.js";

/**
 * Soft coloured halo around a rounded rectangle, drawn with canvas shadows.
 * Place it under the element it should light up and give it that element's
 * bounds: the halo spreads outwards by roughly `blur`.
 */
export class UIRoundedGlow extends UIElement
{
  private static readonly SHADOW_ONLY_OFFSET = 10000;

  private readonly color: string;
  private readonly corner_radius: number;
  private readonly blur: number;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    corner_radius: number,
    blur: number
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.color = color;
    this.corner_radius = corner_radius;
    this.blur = blur;
    this.setClickable( false );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const w = this.rect.getW();
    const h = this.rect.getH();

    this.ctx.save();
    this.ctx.fillStyle = this.color;
    this.ctx.shadowColor = this.color;

    // Only the blurred shadow is wanted, never the solid shape: the shape is
    // drawn far to the side and the shadow offset brings the halo back. The
    // offset is in device pixels while the shape follows the current
    // transform, hence the division by the horizontal scale.
    const scale_x = this.ctx.getTransform().a || 1;
    const offset = UIRoundedGlow.SHADOW_ONLY_OFFSET;
    this.ctx.shadowOffsetX = offset;
    this.ctx.shadowOffsetY = 0;
    const x = -w / 2 - offset / scale_x;

    // A wide pass for the spread, then tighter ones stacking up a bright rim.
    for ( const blur of [ this.blur, this.blur * 0.6, this.blur * 0.3 ] )
    {
      this.ctx.shadowBlur = blur;
      this.ctx.beginPath();
      this.ctx.roundRect( x, -h / 2, w, h, this.corner_radius );
      this.ctx.fill();
    }

    this.ctx.restore();
    this.finishRender();
  }

  public name(): string
  {
    return "UIRoundedGlow";
  }
}
